import {type ReleaseDocument} from '@sanity/client'
import {ErrorOutlineIcon, PinFilledIcon, PinIcon, WarningOutlineIcon} from '@sanity/icons'
import {Box, Card, Container, Flex, Stack, Text} from '@sanity/ui'
import {useCallback, useEffect, useRef, useState} from 'react'

import {Button} from '../../../../ui-components/button/Button'
import {ToneIcon} from '../../../../ui-components/toneIcon/ToneIcon'
import {TextWithTone} from '../../../components/textWithTone/TextWithTone'
import {Details} from '../../../form/components/Details'
import {useTranslation} from '../../../i18n'
import {usePerspective} from '../../../perspective/usePerspective'
import {useSetPerspective} from '../../../perspective/useSetPerspective'
import {useWorkspace} from '../../../studio/workspace'
import {releasesLocaleNamespace} from '../../i18n'
import {useReleaseOperations} from '../../store/useReleaseOperations'
import {useReleasePermissions} from '../../store/useReleasePermissions'
import {getReleaseIdFromReleaseDocumentId} from '../../util/getReleaseIdFromReleaseDocumentId'
import {isNotArchivedRelease} from '../../util/util'
import {ArchivedReleaseBanner} from './ArchivedReleaseBanner'
import {ReleaseDetailsEditor} from './ReleaseDetailsEditor'
import {ReleaseTypePicker} from './ReleaseTypePicker'
import {type DocumentInRelease} from './useBundleDocuments'
import {ValidationProgressIndicator} from './ValidationProgressIndicator'

export function ReleaseDashboardDetails({
  release,
  documents,
}: {
  release: ReleaseDocument
  documents: DocumentInRelease[]
}) {
  const {state} = release

  const releaseId = getReleaseIdFromReleaseDocumentId(release._id)
  const {checkWithPermissionGuard} = useReleasePermissions()
  const {publishRelease, schedule} = useReleaseOperations()

  const {t: tRelease} = useTranslation(releasesLocaleNamespace)
  const {t: tCore} = useTranslation()
  const {selectedReleaseId} = usePerspective()
  const setPerspective = useSetPerspective()

  const isSelected = releaseId === selectedReleaseId
  const releaseFullTitle = release.metadata.title || tCore('release.placeholder-untitled-release')
  const isAtTimeRelease = release?.metadata?.releaseType === 'scheduled'
  const isReleaseOpen = state !== 'archived' && state !== 'published'
  const isActive = release.state === 'active'
  const shouldDisplayError = isActive && typeof release.error !== 'undefined'
  const [shouldDisplayPermissionWarning, setShouldDisplayPermissionWarning] = useState(false)
  const shouldDisplayWarnings = isActive && shouldDisplayPermissionWarning
  const isMounted = useRef(false)
  const {document} = useWorkspace()
  const {
    drafts: {enabled: isDraftModelEnabled},
  } = document
  useEffect(() => {
    isMounted.current = true

    // only run if the release is active
    if (isActive) {
      void checkWithPermissionGuard(publishRelease, release._id).then((hasPermission) => {
        if (isMounted.current) setShouldDisplayPermissionWarning(!hasPermission)
        return null
      })

      // if it's a release that can be scheduled, check if it can be scheduled
      if (release.metadata.intendedPublishAt && isAtTimeRelease) {
        void checkWithPermissionGuard(schedule, release._id, new Date()).then((hasPermission) => {
          if (isMounted.current) setShouldDisplayPermissionWarning(!hasPermission)
          return null
        })
      }
    }
    return () => {
      isMounted.current = false
    }
  }, [
    checkWithPermissionGuard,
    isActive,
    isAtTimeRelease,
    publishRelease,
    release._id,
    release.metadata.intendedPublishAt,
    schedule,
  ])

  const handlePinRelease = useCallback(() => {
    if (isSelected) {
      setPerspective(isDraftModelEnabled ? 'drafts' : 'published')
    } else {
      setPerspective(releaseId)
    }
  }, [isDraftModelEnabled, isSelected, releaseId, setPerspective])

  return (
    <Container width={3}>
      <Stack padding={3} paddingY={[3, 3, 4, 5]}>
        <Flex gap={1} align="center">
          {isReleaseOpen && (
            <Button
              icon={isSelected ? PinFilledIcon : PinIcon}
              tooltipProps={{
                placement: 'top',
                content: isSelected
                  ? tRelease('dashboard.details.unpin-release')
                  : tRelease('dashboard.details.pin-release'),
              }}
              mode="bleed"
              onClick={handlePinRelease}
              radius="full"
              selected={isSelected}
              aria-label={
                isSelected
                  ? `${tRelease('dashboard.details.unpin-release')}: "${releaseFullTitle}"`
                  : `${tRelease('dashboard.details.pin-release')}: "${releaseFullTitle}"`
              }
              aria-live="assertive"
            />
          )}
          {isNotArchivedRelease(release) && <ReleaseTypePicker release={release} />}
          <ValidationProgressIndicator documents={documents} />
          {shouldDisplayError && (
            <Flex gap={2} padding={2} data-testid="release-error-details">
              <Text size={1}>
                <ToneIcon icon={ErrorOutlineIcon} tone="critical" />
              </Text>
              <TextWithTone size={1} tone="critical">
                {isAtTimeRelease
                  ? tRelease('failed-schedule-title')
                  : tRelease('failed-publish-title')}
              </TextWithTone>
            </Flex>
          )}
          {shouldDisplayWarnings && (
            <Flex gap={2} padding={2} data-testid="release-permission-error-details">
              <Text size={1}>
                <ToneIcon icon={WarningOutlineIcon} tone="caution" />
              </Text>
              <TextWithTone size={1} tone="caution">
                {tRelease('permission-missing-title')}
              </TextWithTone>
            </Flex>
          )}
        </Flex>
        <Box padding={2}>
          <ReleaseDetailsEditor release={release} />
        </Box>
        {shouldDisplayError && (
          <Card padding={4} radius={4} tone="critical">
            <Flex gap={3}>
              <Text size={1}>
                <ErrorOutlineIcon />
              </Text>
              <Stack space={4}>
                <Text size={1} weight="semibold">
                  {isAtTimeRelease
                    ? tRelease('failed-schedule-title')
                    : tRelease('failed-publish-title')}
                </Text>
                <Details title={tRelease('error-details-title')}>
                  <Text size={1} accent>
                    <code>{release.error?.message}</code>
                  </Text>
                </Details>
              </Stack>
            </Flex>
          </Card>
        )}

        {shouldDisplayWarnings && (
          <Card padding={4} radius={4} tone="caution">
            <Flex gap={3}>
              <Text size={1}>
                <WarningOutlineIcon />
              </Text>
              <Stack space={3}>
                <Text size={1}>{tRelease('permission-missing-title')}</Text>
                <Text size={1} muted>
                  {tRelease('permission-missing-description')}
                </Text>
              </Stack>
            </Flex>
          </Card>
        )}

        {!isReleaseOpen && <ArchivedReleaseBanner release={release} />}
      </Stack>
    </Container>
  )
}
